import { PortalCustomizationSettings } from '~/api/types/customisationData.ts';
import logger from '~/utils/logger';
import { getBorderRadiusForOption } from '~/pages/Customizations/utils.ts';
import { waitForElement } from '~/utils/commonUtils.ts';

enum BrandOverrideProperties {
  BUTTON_COLOR = 'buttonColor',
  BUTTON_HOVER_COLOR = 'buttonHoverColor',
  TEXT_COLOR = 'textColor',
  LOGIN_BG_COLOR = 'loginBgColor',
  LOGIN_BG_OVERRIDE = 'pageBackgroundCss'
}

type ColorOverrideConfig = {
  configKey: BrandOverrideProperties;
  cssVarName: string;
};

const COLOR_OVERRIDE_LIST: ColorOverrideConfig[] = [
  {
    configKey: BrandOverrideProperties.BUTTON_COLOR,
    cssVarName: '--colors-accent-default'
  },
  {
    configKey: BrandOverrideProperties.BUTTON_HOVER_COLOR,
    cssVarName: '--colors-accent-emphasized'
  },
  {
    configKey: BrandOverrideProperties.TEXT_COLOR,
    cssVarName: '--colors-accent-fg'
  },
  {
    configKey: BrandOverrideProperties.LOGIN_BG_COLOR,
    cssVarName: '--login-page-bg'
  },
  {
    configKey: BrandOverrideProperties.LOGIN_BG_OVERRIDE,
    cssVarName: '--login-page-bg'
  }
];

function applyColorOverrides(
  portalCustomizationSettings: PortalCustomizationSettings
) {
  const root = document.documentElement;
  for (const config of COLOR_OVERRIDE_LIST) {
    portalCustomizationSettings[config.configKey] &&
      root.style.setProperty(
        config.cssVarName,
        portalCustomizationSettings[config.configKey]!
      );
  }
}

function applyBorderRadius(
  portalCustomizationSettings: PortalCustomizationSettings
) {
  if (!portalCustomizationSettings?.borderRadius) {
    return;
  }
  const root = document.documentElement;
  const borderRadiusVariables = getBorderRadiusForOption(
    portalCustomizationSettings?.borderRadius
  );
  if (!borderRadiusVariables) {
    return;
  }
  root.style.setProperty('--radii-l1', `var(${borderRadiusVariables.sm})`);
  root.style.setProperty('--radii-l2', `var(${borderRadiusVariables.md})`);
  root.style.setProperty('--radii-l3', `var(${borderRadiusVariables.lg})`);
}

function applyBrandCustomization(
  portalCustomizationSettings: PortalCustomizationSettings
) {
  logger.info(
    `Applying brand customization for config - `,
    portalCustomizationSettings
  );
  applyColorOverrides(portalCustomizationSettings);
  applyBorderRadius(portalCustomizationSettings);
  // uncomment this when we allow custom CSS
  // applyCssFromStringCspSafe(
  //   portalCustomizationSettings.pageBackgroundCss || ''
  // );
}

/**
 * Parse CSS text with a constructable stylesheet (no DOM injection),
 * walk rules (incl. @media / @supports), and apply declarations inline.
 * Works without 'unsafe-inline' because we never add <style>.
 */
async function applyCssFromStringCspSafe(cssText: string) {
  // 1) Parse using constructable stylesheet (not attached to document)
  if (
    !('CSSStyleSheet' in window) ||
    !('replaceSync' in (CSSStyleSheet.prototype as any))
  ) {
    // Fallback to a safer parser below if constructable stylesheets not supported.
    return fallbackApplyCss(cssText);
  }

  const sheet = new CSSStyleSheet();
  sheet.replaceSync(cssText); // just parses; not added to document

  // 2) Walk rules recursively
  const walk = (rules: CSSRuleList) => {
    for (const rule of Array.from(rules)) {
      switch (rule.type) {
        case CSSRule.STYLE_RULE: {
          const r = rule as CSSStyleRule;
          applyStyleRule(r.selectorText, r.style);
          break;
        }
        case CSSRule.MEDIA_RULE: {
          const mr = rule as CSSMediaRule;
          if (matchMedia(mr.conditionText).matches) walk(mr.cssRules);
          break;
        }
        case CSSRule.SUPPORTS_RULE: {
          const sr = rule as CSSSupportsRule;
          // Note: CSS.supports() takes a declaration or property+value form.
          // The conditionText may be a complex supports condition; browsers
          // evaluate it when the rule exists, so we just traverse it.
          walk(sr.cssRules);
          break;
        }
        // @document / @layer / @container could be added similarly if needed.
        default:
          // Ignore @font-face, @keyframes, etc.—cannot be expressed inline.
          break;
      }
    }
  };

  walk(sheet.cssRules);
}

/** Apply a single CSSStyleRule’s declarations to all matching elements inline. */
const applyStyleRule = (() => {
  // WeakMap: element -> (selector -> applied properties)
  const appliedProps = new WeakMap<HTMLElement, Map<string, Set<string>>>();

  return async (selector: string, style: CSSStyleDeclaration) => {
    await waitForElement(selector);
    let elements: NodeListOf<HTMLElement>;
    try {
      elements = document.querySelectorAll<HTMLElement>(selector);
    } catch {
      return; // skip invalid selector
    }

    elements.forEach((el) => {
      const selectorMap =
        appliedProps.get(el) ?? new Map<string, Set<string>>();
      const prevProps = selectorMap.get(selector) ?? new Set<string>();
      const newProps = new Set<string>();

      // Apply new properties
      for (let i = 0; i < style.length; i++) {
        const prop = style.item(i);
        const value = style.getPropertyValue(prop);
        const priority = style.getPropertyPriority(prop);
        el.style.setProperty(prop, value, priority);
        newProps.add(prop);
      }

      // Remove properties that were applied before but not now
      for (const prop of prevProps) {
        if (!newProps.has(prop)) {
          el.style.removeProperty(prop);
        }
      }

      // Update tracking
      selectorMap.set(selector, newProps);
      appliedProps.set(el, selectorMap);
    });
  };
})();

/** Fallback parser: handles comments, multi-selectors, !important (basic). */
function fallbackApplyCss(cssText: string) {
  // remove comments
  const withoutComments = cssText.replace(/\/\*[\s\S]*?\*\//g, '');
  // match blocks: selector { declarations }
  const blockRe = /([^{]+)\{([^}]*)\}/g;
  let m: RegExpExecArray | null;

  while ((m = blockRe.exec(withoutComments))) {
    const selectorList = m[1].trim();
    const decls = m[2].trim();

    // Handle comma-separated selectors: .a, .b, #c
    const selectors = selectorList.split(',').map((s) => s.trim());

    // Split declarations respecting semicolons
    const pairs = decls
      .split(';')
      .map((s) => s.trim())
      .filter(Boolean)
      .map((line) => {
        // prop: value [!important]
        const [propRaw, ...rest] = line.split(':');
        if (!propRaw || rest.length === 0) return null;
        let value = rest.join(':').trim();
        let priority = '';
        if (/\!important$/i.test(value)) {
          value = value.replace(/\!important$/i, '').trim();
          priority = 'important';
        }
        return { prop: propRaw.trim(), value, priority };
      })
      .filter(
        (x): x is { prop: string; value: string; priority: '' | 'important' } =>
          !!x
      );

    // Apply to each selector
    for (const sel of selectors) {
      let nodes: NodeListOf<HTMLElement>;
      try {
        nodes = document.querySelectorAll<HTMLElement>(sel);
      } catch {
        continue; // skip invalid selectors safely
      }
      nodes.forEach((el) => {
        for (const { prop, value, priority } of pairs) {
          el.style.setProperty(prop, value, priority);
        }
      });
    }
  }
}

export { applyBrandCustomization, applyCssFromStringCspSafe };
